import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import Pagination from "../components/Pagination/Pagination";
import Meta from "../components/Meta";
import PropertyCard from "../components/Property/Property";
import PropertyFilter from "../components/Filters/PropertyFilters";
import { HttpClient } from "../utils/HttpClient";
import { useNavigationHistory } from "../hooks/useNavigationHistory";
import {
  PropertiesPage,
  PropertiesPageList,
} from "../components/Property/PropertyElements";
import {
  HeadingSecondary,
  PageHeaderOverlay,
  PageHeaderContainer,
} from "../components/HeadingElements";
import { MainContainer } from "../components/Styles";
import { FlexContainer } from "../components/ContainerElements";
import Loader from "../components/Loader";
import styled from "styled-components";

const BackButtonContainer = styled.div`
  padding: 20px ${(props) => props.theme?.spacing?.lg || "20px"} 0 0;
  margin-top: 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f0f0;
  border: 1.5px solid #e0e0e0;
  color: #333;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #cc9900;
    color: #fff;
    border-color: #cc9900;
    transform: translateX(-4px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Properties = () => {
  const location = useLocation();
  const { handleGoBack, navigationHistory } = useNavigationHistory();
  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [propertyType, setPropertyType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasHistory, setHasHistory] = useState(false);
  const propertiesPerPage = 4;

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setPropertyType(queryParams.get("property_type"));

    const fetchProperties = async () => {
      setLoading(true);
      const httpClient = new HttpClient();
      const params = {
        property_type: queryParams.get("property_type"),
        category: queryParams.get("category"),
        location: queryParams.get("location"),
      };

      try {
        const response = await httpClient.get("/properties/all", { params });
        setProperties(response.data.data.properties || []);
      } catch (error) {
        console.error("Error fetching properties:", error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [location.search]);

  useEffect(() => {
    // Check if there's a previous page AND it's not the properties page itself
    const previousPage = navigationHistory.getPreviousPage();
    const shouldShowBackButton =
      previousPage !== null && previousPage.path !== location.pathname;
    setHasHistory(shouldShowBackButton);
  }, [navigationHistory, location.pathname]);

  const indexOfLastProperty = currentPage * propertiesPerPage;
  const indexOfFirstProperty = indexOfLastProperty - propertiesPerPage;
  const currentProperties = properties.slice(
    indexOfFirstProperty,
    indexOfLastProperty,
  );

  const totalPages = Math.ceil(properties.length / propertiesPerPage);

  const counts = properties.reduce((acc, property) => {
    const { town, suburb } = property;
    if (!acc[town]) {
      acc[town] = {};
    }
    if (!acc[town][suburb]) {
      acc[town][suburb] = 0;
    }
    acc[town][suburb] += 1;
    return acc;
  }, {});

  const countsArray = Object.entries(counts).map(([town, suburbs]) => ({
    town,
    suburbs: Object.entries(suburbs).map(([suburb, propertyCount]) => ({
      suburb,
      propertyCount,
    })),
  }));

  const handleSuburbSelect = (suburb) => {
    const filteredProperties = properties.filter(
      (property) => property.suburb === suburb,
    );
    setProperties(filteredProperties);
    setCurrentPage(1);
  };

  return (
    <>
      <Meta
        title={"Properties"}
        description={"Find your preferred properties"}
      />
      <div className="">
        <PageHeaderContainer>
          <PageHeaderOverlay />
          <HeadingSecondary fontColor="#fff">
            {propertyType === "sale"
              ? "Properties for sale"
              : "Properties for rent"}
          </HeadingSecondary>
        </PageHeaderContainer>
        <MainContainer>
          {hasHistory && (
            <BackButtonContainer>
              <BackButton
                onClick={handleGoBack}
                title="Go back to previous page"
              >
                <FiArrowLeft />
                Back
              </BackButton>
            </BackButtonContainer>
          )}
          {loading ? (
            <Loader />
          ) : (
            <PropertiesPage>
              <PropertiesPageList>
                <FlexContainer>
                  {currentProperties.map((property) => (
                    <PropertyCard
                      flexBasis={44}
                      key={property.id}
                      propertyId={property.id}
                      imageUrl={property.Images?.[0]?.url || ""}
                      propertyType={property.property_type}
                      postedDate={new Date(
                        property.created_at,
                      ).toLocaleDateString()}
                      category={property.category}
                      price={property.price}
                      name={property.name}
                      propertyName={property.property_name}
                      address={`${property.town}, ${property.suburb}`}
                      town={property.town}
                      suburb={property.suburb}
                      bedrooms={property.bedrooms}
                      bathrooms={property.bathrooms}
                      landSize={parseInt(property.land_size) || 0}
                    />
                  ))}
                </FlexContainer>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </PropertiesPageList>

              <PropertyFilter
                counts={countsArray}
                selectedPropertyType={propertyType}
                onSuburbSelect={handleSuburbSelect}
              />
            </PropertiesPage>
          )}
        </MainContainer>
      </div>
    </>
  );
};

export default Properties;
